import { useMemo } from 'react';
import { MultiValue, OnChangeValue, SingleValue } from 'react-select';

import { useEventHandler } from '../../../../../hooks/useEventHandler';
import { Nullable } from '../../../../../types';
import { findSelectedOption } from '../../logic/findSelectedOption';
import {
  ControlledSelectGroup,
  ControlledSelectOption,
  ControlledSelectValue,
} from '../../types/ControlledSelectOption';
import { ControlledSelectOnChange } from '../../types/ControlledSelectProps';
import { getReactSelectComponentsOverrides } from './logic/getReactSelectComponentsOverrides';
import { getSanitizedControlledSelectValue } from './logic/getSanitizedControlledSelectValue';

export const useControlledSelect = <IsMulti extends boolean = false>({
  value,
  options,
  groups,
  onChange,
  isMulti = false as IsMulti,
}: {
  value: Nullable<ControlledSelectValue<IsMulti>>;
  options: ControlledSelectOption[] | undefined;
  groups: ControlledSelectGroup[] | undefined;
  onChange: ControlledSelectOnChange<IsMulti>;
  isMulti: IsMulti;
}) => {
  const handleChange = useEventHandler(
    (newValue: OnChangeValue<ControlledSelectOption, IsMulti>) => {
      if (isMulti) {
        onChange(
          (newValue as MultiValue<ControlledSelectOption>).map(
            (o) => o.value,
          ) as ControlledSelectValue<IsMulti>,
        );
      } else {
        onChange(
          ((newValue as SingleValue<ControlledSelectOption>)?.value ??
            null) as Nullable<ControlledSelectValue<IsMulti>>,
        );
      }
    },
  );

  const sanitizedValue = useMemo(
    () => getSanitizedControlledSelectValue(value, isMulti),
    [value, isMulti],
  );

  const selectedOption = useMemo(
    () => findSelectedOption(options, groups, sanitizedValue, isMulti),
    [sanitizedValue, options, groups, isMulti],
  );

  const formatGroupLabel = useEventHandler(
    (group: ControlledSelectGroup): string => group.label,
  );

  const components = useMemo(
    () => getReactSelectComponentsOverrides<IsMulti>(),
    [],
  );

  return {
    components,
    handleChange,
    formatGroupLabel,
    selectedOption,
  };
};
